import { useCallback, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import { uploadRecordedLienDocument } from '../../../../lib/fetchApi'
import { validateBoxUploadFile } from '../../../../lib/docs'
import { notificationActions } from '../../../../store/notifications'
import Button from '../../../common/Button'
import HoverWrapperInfo from '../../../wrappers/HoverWrapperInfo'

const UPLOAD_TITLE = 'Upload recorded file?'
const UPLOAD_MESSAGE =
    'Are you sure you want to upload a recorded file? This will replace the original case document file.'

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
}

const cardStyle = {
    backgroundColor: 'var(--background, var(--white, #fff))',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 320,
    maxWidth: 'min(420px, 92vw)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
}

const titleStyle = { margin: '0 0 8px', fontSize: 16, fontWeight: 600 }
const textStyle = { margin: '0 0 16px', fontSize: 14, lineHeight: 1.45, color: 'var(--subtitle, #555)' }
const actionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }

function UploadRecordedConfirmModal({ onConfirm, onCancel }) {
    const overlayRoot = typeof document !== 'undefined' ? document.getElementById('overlay-root') : null
    if (!overlayRoot) {
        return null
    }

    return ReactDOM.createPortal(
        <div style={overlayStyle} role="presentation" onClick={onCancel}>
            <div
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="lien-upload-recorded-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id="lien-upload-recorded-title" style={titleStyle}>{UPLOAD_TITLE}</h3>
                <p style={textStyle}>{UPLOAD_MESSAGE}</p>
                <div style={actionsStyle}>
                    <Button click={onCancel} btnStyle="btn_white">Cancel</Button>
                    <Button click={onConfirm}>Upload</Button>
                </div>
            </div>
        </div>,
        overlayRoot,
    )
}

export default function UploadRecordedLienDocument({ caseLienId, side }) {
    const inputRef = useRef(null)
    const pendingFileRef = useRef(null)
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const userToken = useSelector((state) => state.user.token)
    const activeCase = useSelector((state) => state.case.activeCase)

    const uploadFile = useCallback(async (file) => {
        const sizeError = validateBoxUploadFile(file)
        if (sizeError) {
            dispatch(notificationActions.setError({
                errorMessage: `"${file.name}" is too large. ${sizeError}`,
            }))
            return
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('case_lien_id', String(caseLienId))
        formData.append('side', side)

        setLoading(true)
        const response = await uploadRecordedLienDocument(
            userToken,
            activeCase.case.id,
            formData,
            dispatch
        )
        setLoading(false)

        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        } else {
            dispatch(notificationActions.setError({
                errorMessage: 'File uploaded successfully.',
                type: 'success',
            }))
        }
    }, [activeCase.case.id, caseLienId, dispatch, side, userToken])

    const onFileSelected = (event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) {
            return
        }

        pendingFileRef.current = file
        setConfirmOpen(true)
    }

    const handleConfirm = async () => {
        const file = pendingFileRef.current
        pendingFileRef.current = null
        setConfirmOpen(false)
        if (file) {
            await uploadFile(file)
        }
    }

    const handleCancel = () => {
        pendingFileRef.current = null
        setConfirmOpen(false)
    }

    const openFilePicker = () => {
        inputRef.current?.click()
    }

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.tif,.tiff,image/*"
                onChange={onFileSelected}
                style={{ display: 'none' }}
            />
            <HoverWrapperInfo modalText="Upload recorded file">
                <Button
                    click={openFilePicker}
                    isLoading={loading}
                    btnStyle="btn_small_icon"
                    title="Upload recorded file"
                >
                    <i className="ti ti-upload" />
                </Button>
            </HoverWrapperInfo>
            {confirmOpen ? (
                <UploadRecordedConfirmModal onConfirm={handleConfirm} onCancel={handleCancel} />
            ) : null}
        </>
    )
}
